import { nowIso, type MaintenanceRequestRow, type WorkOrderPriority, type WorkOrderRow, type WorkOrderStatus } from '@routeback/core';
import type { Repos } from '../../lib/app';
import { HAS_CLOUD } from '../../lib/supabase';
import { todayIn } from '../timeoff/timeoff';
import { addDays, effectiveDue, milesOn, truckMileage } from './mileage';
import { linkToRule, type RecurringLink } from './recurring';
import { isOpen, requestStatusFor } from './workorders';

/**
 * Manager actions on work orders and the driver reports behind them. The
 * server makes reports follow their job too (private.work_orders_follow) —
 * doing it here as well shows the change at once, offline included.
 */

type Who = { repos: Repos; userId: string };

export function blankWorkOrder(repos: Repos, f: Partial<WorkOrderRow>): WorkOrderRow {
  return repos.workOrders.blank({
    number: null, truck_id: null, title: '', description: '', status: 'open', priority: 'normal', vendor: '', cost_cents: null,
    odometer: null, opened_on: todayIn(), completed_on: null, completed_by: null, work_done: '', notes: [], for_mechanic: false,
    mechanic_id: null, include_mmr: false, reported_by: null, recurring_task_id: null, due_on: null, ...f,
    // due_miles / due_calendar are left off unless a mileage rule sets them (0045): an older database would refuse the columns.
  });
}

/** Apply a change to the stored row (not a possibly stale copy from the screen). */
export async function patchJob(repos: Repos, id: string, patch: Partial<WorkOrderRow>): Promise<WorkOrderRow | null> {
  const cur = await repos.workOrders.get(id);
  return cur ? repos.workOrders.save({ ...cur, ...patch }) : null;
}

async function follow({ repos, userId }: Who, woId: string, status: WorkOrderStatus, reqs: MaintenanceRequestRow[]): Promise<void> {
  const s = requestStatusFor(status);
  for (const r of reqs.filter((x) => x.work_order_id === woId && x.status !== 'cancelled' && x.status !== s)) {
    await repos.maintenanceRequests.save({ ...r, status: s, status_at: nowIso(), status_by: userId });
  }
}

/** Turn a driver's report into a new job. */
export async function makeJobFromReport(who: Who, r: MaintenanceRequestRow, priority: WorkOrderPriority): Promise<WorkOrderRow> {
  const wo = await who.repos.workOrders.save(blankWorkOrder(who.repos, {
    truck_id: r.truck_id, title: r.category, description: r.description, priority, reported_by: r.user_id,
    opened_on: r.created_at.slice(0, 10),
  }));
  await attachReport(who, r, wo);
  return wo;
}

/** A second report of the same problem goes on the existing job. */
export async function attachReport({ repos, userId }: Who, r: MaintenanceRequestRow, wo: WorkOrderRow): Promise<void> {
  await repos.maintenanceRequests.save({ ...r, work_order_id: wo.id, status: requestStatusFor(wo.status), status_at: nowIso(), status_by: userId });
}

export async function declineReport({ repos, userId }: Who, r: MaintenanceRequestRow, note: string): Promise<void> {
  await repos.maintenanceRequests.save({ ...r, status: 'declined', manager_note: note.trim() || null, status_at: nowIso(), status_by: userId });
}

export async function setJobStatus(who: Who, wo: WorkOrderRow, status: WorkOrderStatus, reqs: MaintenanceRequestRow[]): Promise<void> {
  const reopened = wo.status === 'done' && status !== 'done';
  await patchJob(who.repos, wo.id, { status, ...(reopened ? { completed_on: null, completed_by: null } : {}) });
  await follow(who, wo.id, status, reqs);
}

export interface Completion {
  vendor: string; completed_on: string; odometer: number | null; cost_cents: number | null; work_done: string; include_mmr: boolean;
  /** For a job no rule booked: whether it counts as a recurring service. */
  recurring?: RecurringLink;
}

export async function completeJob(who: Who, wo: WorkOrderRow, c: Completion, reqs: MaintenanceRequestRow[], wos: WorkOrderRow[] = []): Promise<void> {
  const { recurring, ...fields } = c;
  const ruleId = !wo.recurring_task_id && recurring && wo.truck_id ? await linkToRule(who.repos, recurring, wo.truck_id, wo.id, wos) : null;
  const saved = await patchJob(who.repos, wo.id, {
    ...fields, status: 'done', completed_by: wo.status === 'done' ? wo.completed_by : who.userId, ...(ruleId ? { recurring_task_id: ruleId } : {}),
  });
  await follow(who, wo.id, 'done', reqs);
  // The server books the next scheduled service (private.work_orders_schedule_next); with no server, do it here.
  if (!HAS_CLOUD && saved?.recurring_task_id && (wo.status !== 'done' || ruleId)) await bookNextLocally(who.repos, saved, c.completed_on, c.odometer);
  // Likewise the truck's mileage (server: private.work_orders_follow).
  if (!HAS_CLOUD && c.odometer != null && wo.truck_id && (wo.status !== 'done' || wo.odometer !== c.odometer)) {
    await who.repos.odometerReadings.save(who.repos.odometerReadings.blank({
      truck_id: wo.truck_id, reading: c.odometer, read_on: c.completed_on, month: `${c.completed_on.slice(0, 7)}-01`, source: 'work_order',
    }));
  }
}

export async function bookNextLocally(repos: Repos, wo: WorkOrderRow, completedOn: string, odometer: number | null = null): Promise<void> {
  const task = await repos.recurringTasks.get(wo.recurring_task_id!);
  if (!task || !task.active || task.deleted_at || (!task.interval_days && !task.interval_miles)) return;
  if (!task.applies_to_all && !(await repos.recurringTaskTrucks.listLive()).some((m) => m.task_id === task.id && m.truck_id === wo.truck_id)) return;
  if ((await repos.workOrders.listLive()).some((w) => w.recurring_task_id === task.id && w.truck_id === wo.truck_id && isOpen(w))) return;
  // Same as private.work_orders_schedule_next: days from the completion date, miles from the odometer entered (or the estimate that day).
  const cal = task.interval_days ? addDays(completedOn, task.interval_days) : null;
  let due: Pick<WorkOrderRow, 'due_on' | 'due_miles' | 'due_calendar'> = { due_on: cal };
  if (task.interval_miles && wo.truck_id) {
    const m = truckMileage(await repos.odometerReadings.listLive(), wo.truck_id);
    const at = odometer ?? milesOn(m, completedOn);
    const w = { due_on: null, due_miles: at != null ? at + task.interval_miles : null, due_calendar: cal };
    due = { ...w, due_on: w.due_miles != null ? effectiveDue(w, m) : cal };
  }
  await repos.workOrders.save(blankWorkOrder(repos, {
    truck_id: wo.truck_id, title: task.name, priority: wo.priority, ...due, recurring_task_id: task.id,
    for_mechanic: wo.for_mechanic, mechanic_id: wo.mechanic_id,
  }));
}

/** The plugin's guard: a finished scheduled job can't be reopened while the next one is open (the database refuses it too). */
export function blocksReopen(wo: WorkOrderRow, all: WorkOrderRow[]): WorkOrderRow | undefined {
  if (!wo.recurring_task_id) return undefined;
  return all.find((w) => w.id !== wo.id && w.recurring_task_id === wo.recurring_task_id && w.truck_id === wo.truck_id && isOpen(w));
}
